import { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { NodeProps } from 'reactflow';
import { useMutation } from '@tanstack/react-query';
import * as Combobox from '@givebutter/primitives/combobox';
import * as RadioGroup from '@givebutter/primitives/radio-group';
import { faHashtag } from '@fortawesome/pro-regular-svg-icons';
import { faTag } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createTags } from 'api/tags';
import {
    TagContactFieldValues,
    TagContactNodeData,
} from 'assets/js/pages/engage/workflows/actions/types';
import * as CanvasNode from 'assets/js/pages/engage/workflows/components/CanvasNode';
import { DeleteCanvasNodeMenuItem } from 'assets/js/pages/engage/workflows/components/DeleteCanvasNodeMenuItem';
import { DuplicateNodeMenuItem } from 'assets/js/pages/engage/workflows/components/DuplicateNodeMenuItem';
import * as NodeBadges from 'assets/js/pages/engage/workflows/components/NodeBadges';
import { StepFormContainer } from 'assets/js/pages/engage/workflows/components/StepFormContainer';
import { useDebouncedAutosave } from 'assets/js/pages/engage/workflows/hooks/useDebouncedAutosave';
import { useSyncFormToNode } from 'assets/js/pages/engage/workflows/hooks/useSyncFormToNode';
import { useTriggerValidationOnDeselection } from 'assets/js/pages/engage/workflows/hooks/useTriggerValidationOnDeselection';
import { getActionNodeHeaderDetails } from 'assets/js/pages/engage/workflows/utils/getActionNodeHeaderDetails';
import * as DropdownMenu from 'components/dropdown-menu';
import * as Form from 'components/form';
import { WorkflowActionTypeEnum } from 'constants/workflows';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useSuspenseCurrentAccountTags } from 'customHooks/useCurrentAccountTags';
import { sortBy, uniq } from 'lodash';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { Tag } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface TagContactActionNodeProps extends NodeProps<TagContactNodeData> {}
const TagContact = ({ id, selected, data: { formData } }: TagContactActionNodeProps) => {
    const methods = Form.useGivebutterForm<TagContactFieldValues>({
        mode: 'all',
        defaultValues: {
            tags: formData?.values?.tags ?? [],
            action: formData?.values.action ?? 'tag',
        },
    });
    const {
        control,
        formState: { errors },
        path,
        watch,
        trigger,
        getValues,
    } = methods;
    const tagActionType = watch('action');
    const isError = Object.keys(errors).length > 0;

    useSyncFormToNode<TagContactFieldValues>({ control, nodeId: id });
    useDebouncedAutosave<TagContactFieldValues>({ methods });
    useTriggerValidationOnDeselection<TagContactFieldValues>({ trigger, selected });

    return (
        <Form.Provider {...methods}>
            <StepFormContainer>
                <CanvasNode.Root
                    className="max-w-96"
                    isSelected={selected}
                    isError={isError}
                    id={id}
                >
                    <CanvasNode.Container>
                        <CanvasNode.ErrorAlert />
                        <CanvasNode.Header>
                            <CanvasNode.Icon icon={faTag} className="text-green-500" />
                            <div className="flex flex-col items-start">
                                <CanvasNode.Title>
                                    {tagActionType === 'untag' ? 'Untag' : 'Tag'} contact
                                </CanvasNode.Title>
                                {selected ? (
                                    <CanvasNode.Description>
                                        Search for an existing tag or create a new one.
                                    </CanvasNode.Description>
                                ) : null}
                            </div>
                            <DropdownMenu.Root>
                                <CanvasNode.ActionMenuTrigger />
                                <DropdownMenu.Content align="end">
                                    <DuplicateNodeMenuItem />
                                    <DeleteCanvasNodeMenuItem />
                                </DropdownMenu.Content>
                            </DropdownMenu.Root>
                        </CanvasNode.Header>
                        <CanvasNode.Body>
                            <CanvasNode.BodySelected>
                                <Form.UseFormRadioGroup
                                    name="action"
                                    orientation="horizontal"
                                    className="gap-0"
                                    variant={'card'}
                                >
                                    <RadioGroup.Item
                                        value="tag"
                                        className="radix-state-checked:border-r radix-state-checked:bg-blue-25 rounded-r-none border-r-0 py-2 text-center"
                                    >
                                        <span className="group-radix-state-checked:font-medium group-radix-state-checked:text-blue-500 text-gray-900">
                                            Tag
                                        </span>
                                    </RadioGroup.Item>
                                    <RadioGroup.Item
                                        value="untag"
                                        className="radix-state-checked:border-l radix-state-checked:bg-blue-25 rounded-l-none border-l-0 py-2 text-center"
                                    >
                                        <span className="group-radix-state-checked:font-medium group-radix-state-checked:text-blue-500 text-gray-900">
                                            Untag
                                        </span>
                                    </RadioGroup.Item>
                                </Form.UseFormRadioGroup>

                                <Form.Container>
                                    <Form.Label name={path('tags')} className="sr-only">
                                        Tags
                                    </Form.Label>
                                    <TagsSearchableInput />
                                    <Form.Error name={path('tags')} />
                                </Form.Container>
                            </CanvasNode.BodySelected>
                            <CanvasNode.BodyUnselected>
                                <TagContactActionFormValuesDisplay values={getValues()} />
                            </CanvasNode.BodyUnselected>
                        </CanvasNode.Body>
                    </CanvasNode.Container>
                </CanvasNode.Root>
            </StepFormContainer>
        </Form.Provider>
    );
};

interface TagContactFormValuesDisplayProps {
    values: TagContactFieldValues;
    showLabel?: boolean;
}
const TagContactActionFormValuesDisplay = ({
    values,
    showLabel = false,
}: TagContactFormValuesDisplayProps) => {
    const { tags, action } = values;
    const actionHeader = getActionNodeHeaderDetails(WorkflowActionTypeEnum.CONTACT_TAG);
    if (!actionHeader) return null;

    return (
        <div className="flex flex-col gap-2">
            {showLabel ? (
                <p className="text-sm font-medium text-gray-900">
                    {action === 'tag' ? 'Tag contact' : 'Untag Contact'}
                </p>
            ) : null}
            <NodeBadges.Root>
                {(tags || []).map((tag: string) => (
                    <NodeBadges.Badge key={tag} className="flex items-center gap-1.5 text-gray-900">
                        <FontAwesomeIcon icon={faHashtag} size="sm" className="text-gray-400" />
                        {tag}
                    </NodeBadges.Badge>
                ))}
            </NodeBadges.Root>
        </div>
    );
};

const tempCreateTag = 'TEMP_TAG';

const TagsSearchableInput = () => {
    const { accountId } = useCurrentAccount();
    const { data: accountContactTags } = useSuspenseCurrentAccountTags();
    const [inputValue, setInputValue] = useState('');
    const [localTags, setLocalTags] = useState<string[]>([]);
    const formContext = Form.useGivebutterFormContext<TagContactFieldValues>();
    const { control, setValue, watch } = formContext;
    const tagsValue = watch('tags');
    const createItemRef = useRef<HTMLDivElement | null>(null);

    const uniqueTags = uniq([...localTags, ...accountContactTags.map(tag => tag.name)]);
    const tags = sortBy(uniqueTags, t => t.toLowerCase());

    const filteredTags =
        inputValue.length > 0
            ? tags.filter(tag => tag.toLowerCase().trim().includes(inputValue.toLowerCase().trim()))
            : tags;

    const showNewTagListItem =
        inputValue.length > 0 &&
        tags.findIndex(tag => tag.toLowerCase().trim() === inputValue.toLowerCase().trim()) === -1;

    useEffect(() => {
        setLocalTags([]);
    }, [accountContactTags]);

    const { mutate: addNewTags } = useMutation<Tag[], GivebutterAxiosError, { tags: string[] }>({
        mutationFn: ({ tags }) => createTags(accountId, { tags }).then(response => response.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.tags(accountId) });
        },
    });

    useEffect(() => {
        const newTags = tagsValue.filter(t => !accountContactTags.map(tag => tag.name).includes(t));
        if (!newTags.length) return;

        addNewTags({ tags: newTags });
    }, [tagsValue, accountContactTags, addNewTags]);

    return (
        <div className="flex flex-col gap-2">
            <Form.Combobox control={control} name="tags">
                <Combobox.Trigger aria-label="Choose tags">
                    <Combobox.Tags placeholder="Choose tags" />
                </Combobox.Trigger>
                <Combobox.Popover>
                    <Combobox.Search
                        placeholder="Search tags"
                        value={inputValue}
                        onChangeValue={setInputValue}
                        onKeyDown={e => {
                            const value = e.currentTarget.value;
                            if (e.key === 'Enter' && value.length > 0) {
                                e.preventDefault();
                                createItemRef.current?.click();
                            }
                        }}
                    />
                    <Combobox.Items>
                        {showNewTagListItem && (
                            <Combobox.Item
                                value={tempCreateTag}
                                ref={createItemRef}
                                className="text-sm"
                                onSelectItem={({ event, selectedItem, selectedItems }) => {
                                    event.preventDefault();
                                    if (selectedItem === tempCreateTag) {
                                        if (
                                            !selectedItems.find(
                                                item => item.trim() === inputValue.trim(),
                                            )
                                        ) {
                                            flushSync(() => {
                                                setLocalTags(tags => [...tags, inputValue]);
                                                setValue('tags', [...tagsValue, inputValue], {
                                                    shouldDirty: true,
                                                });
                                            });
                                        }
                                    }
                                    setInputValue('');
                                }}
                            >
                                <span className="font-medium text-blue-500">Create</span>
                                <div className="flex items-center gap-1">
                                    <FontAwesomeIcon
                                        icon={faHashtag}
                                        className="text-gray-400"
                                        size="xs"
                                    />
                                    <span className="text-gray-900">{inputValue}</span>
                                </div>
                            </Combobox.Item>
                        )}
                        {filteredTags.map(tag => (
                            <Combobox.Item
                                key={tag}
                                value={tag}
                                onSelectItem={() => setInputValue('')}
                            >
                                <Combobox.CheckboxIndicator />
                                <Combobox.ItemText>
                                    <div className="flex items-center gap-1 text-sm font-medium">
                                        <FontAwesomeIcon
                                            icon={faHashtag}
                                            size="xs"
                                            className="text-gray-400"
                                        />
                                        {tag}
                                    </div>
                                </Combobox.ItemText>
                            </Combobox.Item>
                        ))}
                    </Combobox.Items>
                </Combobox.Popover>
            </Form.Combobox>
        </div>
    );
};

export { TagContact, TagContactActionFormValuesDisplay };
export type { TagContactActionNodeProps };
